import React, { useState, useRef, useEffect } from "react";
import AvatarEditor from "react-avatar-editor";
import * as Styles from "../styles/components/UploadAvatar";
import { UploadAvatarProps } from "../interfaces";

const UploadAvatar: React.FC<UploadAvatarProps> = ({
  onImageConfirmed,
  imageType,
  initialImage,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | File>("");
  const [croppedImage, setCroppedImage] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const editorRef = useRef<AvatarEditor | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (initialImage) {
      if (initialImage.startsWith("data:")) {
        setCroppedImage(initialImage);
      } else if (initialImage.startsWith("http")) {
        setCroppedImage(initialImage);
      } else {
        setCroppedImage(`data:image/jpeg;base64,${initialImage}`);
      }
    } else {
      setCroppedImage("");
    }
  }, [initialImage]);

  const handleOpenModal = () => {
    if (isLoading) return;
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading) return;

    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Por favor, selecione um arquivo de imagem válido.");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert("A imagem deve ter no máximo 5MB.");
        return;
      }

      setSelectedImage(file);
      setIsModalOpen(true);
      setZoom(1);

      // Limpa o input para permitir selecionar o mesmo arquivo novamente
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleConfirm = async () => {
    if (editorRef.current && !isLoading) {
      try {
        setIsLoading(true);

        const canvas = editorRef.current.getImageScaledToCanvas();

        canvas.toBlob(
          async (blob) => {
            if (blob) {
              // Cria um File a partir do blob
              const file: File = new File([blob], `avatar-${Date.now()}.jpg`, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });

              // Converte para base64 para preview
              const reader = new FileReader();
              reader.onload = () => {
                const base64Image = reader.result as string;
                setCroppedImage(base64Image);
              };
              reader.readAsDataURL(blob);

              // Envia o File para o componente pai
              onImageConfirmed(file);

              setIsModalOpen(false);
            }
          },
          "image/jpeg",
          0.8
        );
      } catch (error) {
        console.error("Erro ao processar imagem:", error);
        alert("Erro ao processar a imagem. Tente novamente.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    if (isLoading) return;

    setSelectedImage("");
    setIsModalOpen(false);
    setZoom(1);
  };

  const getImageTypeIcon = () => {
    switch (imageType) {
      case 1:
        return "account_circle";
      case 2:
        return "storefront";
      case 3:
        return "business";
      case 4:
        return "corporate_fare";
      case 5:
        return "inventory_2";
      case 6:
        return "meeting_room";
      default:
        return "photo_camera";
    }
  };

  return (
    <Styles.UploadAvatarWrapper>
      {croppedImage ? (
        <Styles.ImgContainer onClick={handleOpenModal}>
          <Styles.StyledImg
            src={croppedImage}
            alt="foto de perfil."
            style={{ opacity: isLoading ? 0.5 : 1 }}
          />
          {isLoading && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgba(0,0,0,0.7)",
                color: "white",
                padding: "var(--sp-8) var(--sp-16)",
                borderRadius: "4px",
                fontSize: "var(--fs-md)",
                zIndex: 10,
              }}
            >
              Processando...
            </div>
          )}
        </Styles.ImgContainer>
      ) : (
        <Styles.IconContainer onClick={handleOpenModal}>
          <Styles.StyledIcon>
            <Styles.MaterialIcons>{getImageTypeIcon()}</Styles.MaterialIcons>
          </Styles.StyledIcon>
        </Styles.IconContainer>
      )}

      <input
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        id="image-input"
        style={{ display: "none" }}
        ref={fileInputRef}
        disabled={isLoading}
      />

      {isModalOpen && (
        <Styles.CenteredModal open={isModalOpen}>
          <Styles.WhiteBackground>
            <h2 id="avatar-editor-modal-title">Escolha uma imagem.</h2>
            <p id="avatar-editor-modal-description">
              Uma imagem ajuda as pessoas a se reconhecerem e evita erros.
            </p>

            <AvatarEditor
              ref={editorRef}
              image={selectedImage}
              width={200}
              height={200}
              border={10}
              color={[255, 255, 255, 0.2]}
              rotate={0}
              scale={zoom}
              borderRadius={200}
              style={{ width: "100%", height: "100%" }}
            />

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Styles.ProportionSlider
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                color="#4261FF"
                style={{ width: "100%" }}
              />

              <Styles.ButtonWrapper style={{ width: "100%" }}>
                <Styles.Button
                  onClick={handleCancel}
                  style={{ flex: 1 }}
                  disabled={isLoading}
                >
                  Cancelar
                </Styles.Button>
                <Styles.Button
                  onClick={handleConfirm}
                  style={{ flex: 1 }}
                  disabled={isLoading}
                >
                  {isLoading ? "Processando..." : "Confirmar"}
                </Styles.Button>
              </Styles.ButtonWrapper>
            </div>
          </Styles.WhiteBackground>
        </Styles.CenteredModal>
      )}
    </Styles.UploadAvatarWrapper>
  );
};

export default UploadAvatar;
